// src/contexts/NotificationContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { db } from '../utils/firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, writeBatch, getDocs } from 'firebase/firestore';
import { AuthContext } from './AuthContext';

export const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Keep track of notifications being processed to avoid race conditions
  const processingNotifIds = useRef(new Set());
  // Keep a reference to the current notifications for access in callbacks
  const notificationsRef = useRef([]);
  
  // Initialize the ref with an empty array
  useEffect(() => {
    if (!notificationsRef.current) {
      notificationsRef.current = [];
    }
  }, []);
  
  // Update our ref whenever notifications change
  useEffect(() => {
    notificationsRef.current = notifications || [];
  }, [notifications]);
  
  // Calculate unread count from notifications for consistency
  const calculateUnreadCount = useCallback((notifs) => {
    return (notifs || []).filter(n => !n.read).length;
  }, []);

  // Fetch notifications in real-time
  useEffect(() => {
    let unsubscribe = () => {};
    if (currentUser?.uid) {
      setLoading(true);
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', currentUser.uid),
        orderBy('timestamp', 'desc')
        // Optionally add a limit if performance becomes an issue
        // limit(50) 
      );

      unsubscribe = onSnapshot(q, (querySnapshot) => {
        const fetchedNotifications = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const notification = { id: doc.id, ...data };
          
          // If this notification is being processed locally, don't override its state
          if (processingNotifIds.current.has(doc.id)) {
            // Find the notification in current state that's being processed
            // Add safety check for undefined
            const currentNotifications = notificationsRef.current || [];
            const existingNotif = currentNotifications.find(n => n?.id === doc.id);
            if (existingNotif) {
              fetchedNotifications.push(existingNotif);
              return;
            }
          }
          
          fetchedNotifications.push(notification);
        });
        
        setNotifications(fetchedNotifications);
        // Calculate unread count from the actual notifications for consistency
        setUnreadCount(calculateUnreadCount(fetchedNotifications));
        setLoading(false);
      }, (error) => {
        console.error("Error fetching notifications: ", error);
        if (currentUser?.uid) {
            setNotifications([]);
            setUnreadCount(0);
        }
        setLoading(false);
      });

    } else {
      // Clear notifications if user logs out
      unsubscribe();
      setNotifications([]);
      setUnreadCount(0);
      setIsModalOpen(false); // Close modal on logout
      setLoading(false);
      unsubscribe = () => {};
    }

    // Cleanup listener on unmount or user change
    return () => {
      unsubscribe();
    };
  }, [currentUser, calculateUnreadCount]); // Removed notifications from dependencies to avoid circular updates

  const toggleModal = useCallback(() => {
    setIsModalOpen(prev => !prev);
    // Optionally mark as read when opening, or require explicit action
    // if (!isModalOpen && unreadCount > 0) { 
    //   markAllAsRead(); 
    // }
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const markNotificationAsRead = useCallback(async (notification) => {
    if (!currentUser?.uid) return;
    if (!notification || notification.read || !notification.id) return;
    
    try {
      // Mark this notification as being processed to avoid race conditions
      processingNotifIds.current.add(notification.id);
      
      // Optimistic UI update using a single state update
      setNotifications(prev => {
        const updated = prev.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        );
        
        // Update unread count based on the updated notifications
        setUnreadCount(calculateUnreadCount(updated));
        
        return updated;
      });
      
      // Update in Firestore
      const notifRef = doc(db, 'notifications', notification.id);
      await updateDoc(notifRef, { read: true });
      
    } catch (error) {
      console.error("Error marking notification as read: ", error);
      
      // Revert optimistic update if there was an error
      setNotifications(prev => {
        const reverted = prev.map(n => 
          n.id === notification.id ? { ...n, read: false } : n
        );
        
        // Update unread count based on the reverted notifications
        setUnreadCount(calculateUnreadCount(reverted));
        
        return reverted;
      });
    } finally {
      // Remove from processing set after a small delay to ensure snapshots have time to resolve
      setTimeout(() => {
        processingNotifIds.current.delete(notification.id);
      }, 500);
    }
  }, [currentUser, calculateUnreadCount]);

  // Optional: Function to mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!currentUser?.uid || unreadCount === 0) return;
    
    // Add safety check for undefined
    const currentNotifications = notificationsRef.current || [];
    const unread = currentNotifications.filter(n => !n.read);
    if (unread.length === 0) return;

    try {
        // Add all notifications being processed to the set
        unread.forEach(notif => processingNotifIds.current.add(notif.id));
        
        // Optimistic update in a single state update
        setNotifications(prev => {
          const updated = prev.map(n => ({ ...n, read: true }));
          setUnreadCount(0);
          return updated;
        });
        
        // Update in batch
        const batch = writeBatch(db);
        unread.forEach(notif => {
            const notifRef = doc(db, 'notifications', notif.id);
            batch.update(notifRef, { read: true });
        });
        await batch.commit();
    } catch (error) {
        console.error("Error marking all notifications as read: ", error);
        
        // Revert optimistic update if there was an error
        setNotifications(prev => {
          // Add safety check
          const reverted = notificationsRef.current || [];
          setUnreadCount(calculateUnreadCount(reverted));
          return reverted;
        });
    } finally {
        // Clear processing set after a delay
        setTimeout(() => {
          processingNotifIds.current.clear();
        }, 500);
    }
  }, [currentUser?.uid, unreadCount, calculateUnreadCount]); // Removed notifications dependency


  const value = {
    notifications,
    unreadCount,
    isModalOpen,
    loading,
    toggleModal,
    closeModal,
    markNotificationAsRead,
    markAllAsRead, // Include if you want a "mark all" button
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 